import { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { createLocationsColumns, filterLocations } from './utils';
import PowerSiteIcon from '@/assets/icons/power-site.svg?react';
import {
  AdminCardHeader,
  AdminTableBody
} from '@/components/AdminToolComponents/card';
import ContainerLoader from '@/components/AdminToolComponents/containerLoader';
import DataTable from '@/components/DataTable';
import Checkbox from '@/components/Form/Checkbox';
import PlusButton from '@/components/Form/PlusButton';
import SearchInput from '@/components/Form/searchInput';
import Pagination from '@/components/Pagination';
import { PaginationRequestParams } from '@/components/Pagination/usePaginationTokens';
import { adminToolPaths } from '@/constants/adminTool';
import { PermissionsList } from '@/constants/permissionsList';
import adminStyles from '@/containers/AdminToolContainers/styles.module.css';
import { useToast } from '@/context/ToastContext';
import useGetLocationsList from '@/hooks/services/Locations';
import { RootState } from '@/store';
import { getLocationsPermissions } from '@/utils/adminToolPermissions';

function LocationsContainer() {
  const { showToast } = useToast();
  const auditableElements = useSelector(
    (state: RootState) => state.auditableElements?.authorizations?.permissions
  ) as PermissionsList;

  const userGroup = useSelector(
    (state: RootState) => state.auditableElements?.authorizations?.userGroup
  );

  const { canCreate: canCreateLocation } = getLocationsPermissions(
    auditableElements,
    userGroup
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [showActive, setShowActive] = useState(true);
  const [showInactive, setShowInactive] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [paginationParams, setPaginationParams] = useState<{
    pageSize: number;
    pageToken?: string | null;
    pageNumber?: number;
    method: 'token' | 'pageNumber' | 'firstPage';
  }>({
    pageSize: 10,
    method: 'firstPage'
  });
  const {
    data: rawResponse,
    isLoading: loading,
    error: queryError,
    refetch
  } = useGetLocationsList(paginationParams);
  const navigate = useNavigate();

  const locations = rawResponse?.data || [];

  const error = queryError ? (queryError as Error).message : null;

  useMemo(() => {
    if (rawResponse?.info?.pagination) {
      const paginationInfo = rawResponse.info.pagination;
      setCurrentPage(paginationInfo.currentPage || 1);
      setTotalPages(paginationInfo.totalPages || 1);
      setNextPageToken(paginationInfo.nextPageToken || null);
    } else {
      const totalItems = locations.length;
      const calculatedTotalPages = Math.max(
        1,
        Math.ceil(totalItems / pageSize)
      );

      setCurrentPage(1);
      setTotalPages(calculatedTotalPages);
      setNextPageToken(null);
    }
  }, [rawResponse, locations.length, pageSize]);

  const filteredLocations = useMemo(() => {
    return filterLocations(locations, searchTerm, showActive, showInactive);
  }, [locations, searchTerm, showActive, showInactive]);

  const paginatedLocations = useMemo(() => {
    if (rawResponse?.info?.pagination) {
      return filteredLocations;
    }
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredLocations.slice(startIndex, endIndex);
  }, [filteredLocations, currentPage, pageSize, rawResponse]);

  useMemo(() => {
    if (!rawResponse?.info?.pagination) {
      const calculatedTotalPages = Math.max(
        1,
        Math.ceil(filteredLocations.length / pageSize)
      );
      setTotalPages(calculatedTotalPages);
    }
  }, [filteredLocations.length, pageSize, rawResponse]);

  const handlePageChange = (params: PaginationRequestParams) => {
    if (rawResponse?.info?.pagination) {
      setPaginationParams({
        pageSize: params.pageSize,
        pageToken: params.token,
        pageNumber: params.pageNumber,
        method: params.method
      });
    } else {
      if (params.pageNumber) {
        setCurrentPage(params.pageNumber);
      } else if (params.direction === 'next') {
        setCurrentPage(prev => Math.min(prev + 1, totalPages));
      } else if (params.direction === 'previous') {
        setCurrentPage(prev => Math.max(prev - 1, 1));
      }
    }
  };

  const columns = useMemo(() => {
    return createLocationsColumns(navigate);
  }, [navigate]);

  useEffect(() => {
    if (error) {
      showToast(error || 'An error occurred while loading locations', {
        type: 'error',
        autoClose: 5000,
        actionText: 'Try again',
        onActionClick: () => refetch()
      });
    }
  }, [error]);

  if (loading) {
    return <ContainerLoader />;
  }

  return (
    <div className={adminStyles.adminWrapper}>
      <div className={adminStyles.adminContainer}>
        <AdminCardHeader
          left={
            <>
              <PowerSiteIcon className={adminStyles.adminIconHeader} />
              <h2 className={adminStyles.adminTitle}>Locations</h2>
            </>
          }
          center={
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <SearchInput
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search"
              />
              <Checkbox
                checked={showActive}
                onChange={setShowActive}
                label="Active"
              />
              <Checkbox
                checked={showInactive}
                onChange={setShowInactive}
                label="Inactive"
              />
            </div>
          }
          right={
            canCreateLocation ? (
              <PlusButton
                onClick={() => navigate(adminToolPaths.locations.newLocation)}
                label="Add a new location"
              />
            ) : null
          }
        />
        <AdminTableBody>
          <DataTable
            rows={paginatedLocations}
            columns={columns}
            initialState={{
              sorting: {
                field: 'name',
                sort: 'asc'
              }
            }}
          />
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            nextPageToken={nextPageToken}
            onPageChange={handlePageChange}
            useTokens={true}
          />
        </AdminTableBody>
      </div>
    </div>
  );
}

export default LocationsContainer;
